import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TableBody from '@mui/material/TableBody';
import IconButton from '@mui/material/IconButton';
import TableContainer from '@mui/material/TableContainer';
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { fTimestamp } from 'src/utils/format-time';
import { useBoolean } from 'src/hooks/use-boolean';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import {
  useTable,
  getComparator,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom,
  TableSelectedAction,
  TablePaginationCustom,
} from 'src/components/table';
import { getRequest } from 'src/services';
import { LoadingScreen } from 'src/components/loading-screen';
import { useNavigate } from 'react-router';
import { Box } from '@mui/material';
import OrderTableToolbar from './geenral-ledger-report-filter';
import OrderTableRow from './general-ledger-report-table-row';
import Header from '../header';
import { endpoints } from 'src/utils/axios';
import { getCsvData } from './getCsv_general_ledger';
import { CSVLink } from 'react-csv';
// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'Sr.No', label: 'Sr.no' },
  { id: 'Chart of account', label: 'Chart of account' },
  { id: 'Account Name', label: 'Account Name' },
  { id: 'Date', label: 'Date' },
  { id: 'Account Type', label: 'Account Type' },
  { id: 'bank_information', label: 'Bank Name' },
  { id: 'opening_balance', label: 'Opening Balance' },
  { id: 'Credit', label: 'Credit' },
  { id: 'Debit', label: 'Debit' },
  { id: 'Comulative Credit', label: 'Comulative Credit' },
  { id: 'Comulative Debit', label: 'Comulative Debit' },
  { id: 'Balance', label: 'Balance' },
  { id: 'Notes/Remarks', label: 'Notes/Remarks' },
];

const defaultFilters = {
  name: '',
  status: 'all',
  startDate: null,
  endDate: null,
};

// ----------------------------------------------------------------------

export default function GeneralLedgerReport() {
  const table = useTable({ defaultOrderBy: 'orderNumber' });
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const confirm = useBoolean();
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);

  const csvLinkRef = useRef();
  const dateError =
    filters.startDate && filters.endDate
      ? filters.startDate.getTime() > filters.endDate.getTime()
      : false;

  const dataFiltered = useMemo(() => {
    return applyFilter({
      inputData: tableData,
      comparator: getComparator(table.order, table.orderBy),
      filters,
      dateError,
    });
  }, [tableData, table.order, table.orderBy, filters, dateError]);

  const dataInPage = dataFiltered.slice(
    table.page * table.rowsPerPage,
    table.page * table.rowsPerPage + table.rowsPerPage
  );

  const denseHeight = table.dense ? 52 : 72;
  const canReset =
    !!filters.name || filters.status !== 'all' || (!!filters.startDate && !!filters.endDate);
  const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;

  const handleFilters = useCallback(
    (name, value) => {
      table.onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleDeleteRow = useCallback(
    (id) => {
      const deleteRow = tableData.filter((row) => row.id !== id);
      setTableData(deleteRow);

      table.onUpdatePageDeleteRow(dataInPage.length);
    },
    [dataInPage.length, table, tableData]
  );
  const handleDeleteRows = useCallback(() => {
    const deleteRows = tableData.filter((row) => !table.selected.includes(row.id));
    setTableData(deleteRows);

    table.onUpdatePageDeleteRows({
      totalRows: tableData.length,
      totalRowsInPage: dataInPage.length,
      totalRowsFiltered: dataFiltered.length,
    });
  }, [dataFiltered.length, dataInPage.length, table, tableData]);
  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);
  const handleViewRow = useCallback(
    (id) => {
      router.push(paths.dashboard.order.details(id));
    },
    [router]
  );
  const handleFilterStatus = useCallback(
    (event, newValue) => {
      handleFilters('status', newValue);
    },
    [handleFilters]
  );

  useEffect(() => {
    getLedgerData();
  }, []);

  const getCsvHeaders = () => {
    return TABLE_HEAD.filter(({ id }) => id !== 'Sr.No')
      .map(({ id, label }) => ({
        label,
        key: id,
      }));
  };
  const handleExport = () => {
    if (csvLinkRef.current) {
      const csvData = getCsvData(dataFiltered);
      csvLinkRef.current.link.click(csvData);
    }
  };


  const getLedgerData = async () => {
    try {
      setLoading(true);
      const data = await getRequest(endpoints.accounting.general_ledger.get_list);
        setTableData(data);
        console.log(tableData, 'tableData------------------------')
    } catch (error) {
      console.error('Error fetching ledger data:', error);
      // Optionally, set an error state here to display to the user
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <Box sx={{ width: '100%', padding: '20px' }}>
        <Header name="General Ledger Report" />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1,
            borderRadius: 1,
          }}
        >
          <OrderTableToolbar
            filters={filters}
            onFilters={handleFilters}
            //
            //defaultStartDate={defaultStartDate}
            canReset={canReset}
            onResetFilters={handleResetFilters}
          />
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              className="dark-button"
              variant="contained"
              startIcon={<Iconify icon="ic:baseline-email" />}
            //onClick={handleOpenShareModal} // Updated to open modal and generate PDF
            >
              Share By mail
            </Button>
            <Button
              onClick={handleExport}
              className="dark-button"
              variant="contained"
              startIcon={<Iconify icon="solar:export-bold" />}
            >
              Export
            </Button>
            <Button
              className="dark-button"
              variant="contained"
              startIcon={<Iconify icon="mdi:arrow-back-circle" />}
              onClick={() => navigate('/accounts-management/general-ledger')}
            >
              Back
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box height={700}>
            <LoadingScreen />
          </Box>
        ) : (<Card>
          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <TableSelectedAction
              dense={table.dense}
              numSelected={table.selected.length}
              rowCount={tableData.length}
              onSelectAllRows={(checked) =>
                table.onSelectAllRows(
                  checked,
                  tableData.map((row) => row.id)
                )
              }
              action={
                <Tooltip title="Delete">
                  <IconButton color="primary" onClick={confirm.onTrue}>
                    <Iconify icon="solar:trash-bin-trash-bold" />
                  </IconButton>
                </Tooltip>
              }
            />

            <Scrollbar>
              <Table size={table.dense ? 'small' : 'large'} sx={{ minWidth: "100%" }}>
                <TableHeadCustom
                  order={table.order}
                  orderBy={table.orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={tableData.length}
                  numSelected={table.selected.length}
                  onSort={table.onSort}
                  onSelectAllRows={(checked) =>
                    table.onSelectAllRows(
                      checked,
                      tableData.map((row) => row.id)
                    )
                  }
                />

                <TableBody>
                  {dataFiltered
                    .slice(
                      table.page * table.rowsPerPage,
                      table.page * table.rowsPerPage + table.rowsPerPage
                    )
                    .map((row, index) => (
                      <OrderTableRow
                        key={row.id}
                        row={row}
                        selected={table.selected.includes(row.id)}
                        onSelectRow={() => table.onSelectRow(row.id)}
                        onDeleteRow={() => handleDeleteRow(row.id)}
                        serialNumber={table.page * table.rowsPerPage + index + 1}
                      />
                    ))}

                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(table.page, table.rowsPerPage, tableData.length)}
                  />

                  <TableNoData notFound={notFound} />
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>
        </Card>
        )}
        {
          <CSVLink
            ref={csvLinkRef}
            filename="General_Ledger_Report.csv"
            headers={getCsvHeaders()}
            data={getCsvData(tableData)}
            style={{ display: 'none' }}
          />
        }
        <TablePaginationCustom
          count={dataFiltered.length}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          //
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Box>
      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete"
        content={
          <>
            Are you sure want to delete <strong> {table.selected.length} </strong> items?
          </>
        }
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              handleDeleteRows();
              confirm.onFalse();
            }}
          >
            Delete
          </Button>
        }
      />
    </>
  );
}

// ----------------------------------------------------------------------

function applyFilter({ inputData, comparator, filters, dateError }) {
  const { status, name, startDate, endDate } = filters;

  const stabilizedThis = inputData.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map((el) => el[0]);

  if (name) {
    inputData = inputData.filter(
      (order) =>
        order.orderNumber.toLowerCase().indexOf(name.toLowerCase()) !== -1 ||
        order.customer.name.toLowerCase().indexOf(name.toLowerCase()) !== -1 ||
        order.customer.email.toLowerCase().indexOf(name.toLowerCase()) !== -1
    );
  }

  if (status !== 'all') {
    inputData = inputData.filter((order) => order.status === status);
  }

  if (!dateError) {
    if (startDate && endDate) {
      const start = fTimestamp(startDate, true, false);
      const end = fTimestamp(endDate, false, true);

      let invoiceMatches = inputData.filter(order => {
        const invoiceDate = fTimestamp(order?.invoice_date);
        return invoiceDate >= start && invoiceDate <= end;
      });

      let receiptMatches = inputData.filter(order => {
        const receiptDate = fTimestamp(order?.date_of_receipt);
        return receiptDate >= start && receiptDate <= end;
      });

      let dateMatches = inputData.filter(order => {
        const date = fTimestamp(order?.date);
        return date >= start && date <= end;
      });

      const mergedResults = [...invoiceMatches, ...receiptMatches, ...dateMatches];
      inputData = mergedResults;
    }
  }

  return inputData;
}
